import type { GameApi } from '../state/useGame.ts';
import { useState } from 'react';

import { copyFor } from './modeCopy.ts';
import { HUB_HOST, SITES } from '../sites.ts';

function formatDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00Z`);

  if (Number.isNaN(parsed.getTime())) return iso;

  return parsed.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

export function EntryScreen({ game }: { game: GameApi }): React.ReactElement {
  const puzzle = game.puzzle!;
  const best = game.best;
  const copy = copyFor(game.mode.id);
  const [name, setName] = useState(game.name ?? '');

  return (
    <div className="entry">
      <div className="entry__head">
        <p className="wordmark">
          word soup<span className="dot">.</span>
        </p>
      </div>

      <h1 className="entry__title">{copy.title}</h1>

      <p className="entry__meta">
        {copy.label} #{puzzle.displayNumber} · {formatDate(puzzle.date)}
      </p>

      {/* Deliberately NOT a decorative board. Showing a made-up grid here would be
          showing today's puzzle inaccurately, which is worse than showing none. The
          wording is in modeCopy.ts, one entry per mode. */}
      <ul className="entry__rules">
        {copy.rules.map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>

      <div className="entry__spacer" />

      {/* The streak, when there is one. A single day is not a streak and saying so would
          cheapen the word; from two days up it is the reason to come back tomorrow. */}
      {game.streak > 1 ? (
        <p className="entry__streak">
          🔥 {game.streak} day streak
          <span className="entry__streak-note">
            {' '}· finish today's to keep it
          </span>
        </p>
      ) : null}

      {best ? (
        <dl className="entry__best">
          <dt>{best.confirmed ? 'Your best' : 'Your best on this device'}</dt>
          <dd>
            {best.score} · {best.cleared ? 'cleared' : `${best.tilesRemaining} left`}
          </dd>
        </dl>
      ) : null}

      {/* ***********************************************************************************
          The name, BEFORE the game rather than after it.
          
          It used to live inside the leaderboard panel on the finish screen, behind a
          button - so by the time anyone found it their score had already been posted as
          Anonymous. Asked here, it is on the row from the first game.
          *********************************************************************************** */}
      <label className="field entry__name">
        <span className="field__label">Your name on the leaderboard — optional</span>
        <input
          className="field__input"
          type="text"
          value={name}
          maxLength={20}
          placeholder="Anonymous"
          autoComplete="nickname"
          onChange={(event) => setName(event.target.value)}
          onBlur={() => void game.setName(name)}
        />
      </label>

      <div className="entry__actions">
        {game.hasSavedAttempt ? (
          <>
            <button className="button" type="button" onClick={game.resume}>
              Continue
            </button>
            <button className="button button--secondary" type="button" onClick={game.begin}>
              Start again
            </button>
          </>
        ) : (
          <button
            className="button"
            type="button"
            onClick={() => {
              // Tapping Play does not always blur the field first on a phone, and a name
              // typed and then lost is worse than never having asked.
              void game.setName(name);
              game.begin();
            }}
          >
            {copy.play}
          </button>
        )}
      </div>

      {/* The way back to the other versions. There are four now and there will be more,
          and a player who arrived on one of them has no way to discover the rest - the
          sites are deliberately separate, each serving one mode. */}
      <p className="entry__note">
        No account needed. ·{' '}
        <a className="entry__hub" href={HUB_HOST}>
          {SITES.length} versions to try
        </a>
      </p>
    </div>
  );
}
